import React, { useState, useEffect } from 'react';
import { Event, Coordinates } from '../types/event';
import styles from './EventDetailPanel.module.css';
import { eventService } from '../services/api';
import { 
  IconX, 
  IconHeart, 
  IconBookmark, 
  IconCalendarEvent, 
  IconMapPin, 
  IconUsers, 
  IconTag, 
  IconInfoCircle,
  IconChevronLeft,
  IconExternalLink,
  IconCopy,
  IconCheck,
  IconRoute,
  IconEye
} from '@tabler/icons-react';

interface EventDetailPanelProps {
  event: Event | null;
  onClose: () => void;
  onInterestClick: (eventId: number) => void;
  onSaveClick: (eventId: number) => void;
  userInterested?: boolean;
  userSaved?: boolean;
  distanceLabel?: string;
  userLocation?: Coordinates | null;
}

const EventDetailPanel: React.FC<EventDetailPanelProps> = ({ 
  event, 
  onClose, 
  onInterestClick, 
  onSaveClick,
  userInterested = false,
  userSaved = false,
  distanceLabel,
  userLocation
}) => {
  // Initialiser les états locaux à partir des props, en forçant la conversion en booléen
  const [isInterested, setIsInterested] = useState(Boolean(userInterested));
  const [isSaved, setIsSaved] = useState(Boolean(userSaved));
  const [linkCopied, setLinkCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [categoryInfo, setCategoryInfo] = useState<{name: string, color: string}>({name: 'Catégorie', color: '#ADB5BD'});
  const [viewCount, setViewCount] = useState<number>(0);
  
  // Function to get directions to the event location using Google Maps
  const getDirectionsUrl = () => {
    if (!event || !event.coordinates) return null;
    
    // Create Google Maps directions URL
    let url = `https://www.google.com/maps/dir/?api=1&destination=${event.coordinates.lat},${event.coordinates.lon || event.coordinates.lng}`;
    
    // If user location is available, add it as the origin
    if (userLocation && userLocation.lat && (userLocation.lon || userLocation.lng)) {
      url += `&origin=${userLocation.lat},${userLocation.lon || userLocation.lng}`;
    }
    
    return url;
  };
  
  // Charger les informations de la catégorie uniquement si nécessaire
  useEffect(() => {
    if (event) {
      console.log("Événement dans le panel:", event);
      
      // Initialiser le compteur de vues en utilisant la fonction normalisée
      const initialViewCount = getViewCount(event);
      setViewCount(initialViewCount);
      console.log(`EventDetailPanel: Initialisation du compteur de vues pour l'événement #${event.id}:`, initialViewCount);
      
      // Extraire les informations de la catégorie pour l'affichage
      setCategoryInfo({
        name: event.eventCategory?.name || 'Non classé',
        color: event.eventCategory?.color || '#ADB5BD'
      });
      
      // Enregistrer la vue après l'initialisation du compteur
      recordEventView();
    }
  }, [event]);
  
  // Mise à jour quand les props changent
  useEffect(() => {
    // Forcer la conversion en booléen pour s'assurer qu'on a bien des valeurs booléennes
    if (event) {
      // Log pour déboguer la structure de l'événement, particulièrement le champ category
      console.log('Événement complet dans DetailPanel:', event);
      console.log('État actuel de categoryInfo:', categoryInfo);
      
      setIsInterested(Boolean(event.isInterested));
      setIsSaved(Boolean(event.isSaved));
    }
  }, [event, userInterested, userSaved, categoryInfo]);
  
  // Record a view when the panel is opened
  const recordEventView = async () => {
    try {
      if (!event) return; // Protection contre null
      if (event.id) {
        // Check if we've already viewed this event recently
        const viewedEventsJson = localStorage.getItem('viewedEvents') || '{}';
        const viewedEvents = JSON.parse(viewedEventsJson);
        const eventKey = `event_${event.id}`;
        const lastViewTime = viewedEvents[eventKey];
        const currentTime = Date.now();
        
        // If we haven't viewed it in the last 30 minutes, record a view
        if (!lastViewTime || (currentTime - lastViewTime > 30 * 60 * 1000)) {
          console.log(`Recording view for event ${event.id}`);
          // Record the view time
          viewedEvents[eventKey] = currentTime;
          localStorage.setItem('viewedEvents', JSON.stringify(viewedEvents));
          
          try {
            // Attempt to use the API endpoint if it exists
            const response = await eventService.recordView(event.id);
            const newViewCount = response?.viewCount || (event._count?.views || 0) + 1;
            console.log(`Mise à jour du compteur de vues: ${newViewCount}`);
            setViewCount(newViewCount);
            
            // Also update the event object to ensure consistency
            if (event._count) {
              event._count.views = newViewCount;
            }
            if (event.viewCount !== undefined) {
              event.viewCount = newViewCount;
            }
          } catch (apiError) {
            console.error('API endpoint for recording views not available:', apiError);
            // Fallback: Use the existing view count from the event data
            const currentViewCount = event._count?.views || event.viewCount || 0;
            setViewCount(currentViewCount + 1);
            
            // Update the event object with the incremented count (client-side only)
            if (event._count) {
              event._count.views = currentViewCount + 1;
            }
            if (event.viewCount !== undefined) {
              event.viewCount = currentViewCount + 1;
            }
          }
        } else {
          console.log(`Skipping duplicate view for event ${event.id}, last viewed ${(currentTime - lastViewTime) / 1000}s ago`);
          // Just use the existing count
          setViewCount(event._count?.views || event.viewCount || 0);
        }
      }
    } catch (err) {
      console.error('Erreur lors de l\'enregistrement de la vue:', err);
      // Use the count from the event object as fallback
      if (event) {
        setViewCount(event._count?.views || event.viewCount || 0);
      } else {
        setViewCount(0);
      }
    }
  };
  
  // Fonction pour récupérer le compteur de vues
  const getViewCount = (event: Event) => {
    return event._count?.views || event.viewCount || 0;
  };
  
  if (!event) return null;
  
  const handleInterestClick = () => {
    console.log('Panel - Interest click - Avant:', isInterested, 'event.id:', event.id);
    
    // Inverser l'état local
    const newIsInterested = !isInterested;
    setIsInterested(newIsInterested);
    
    console.log('Panel - Interest click - Après:', newIsInterested);
    
    // Mettre à jour localement le compteur d'intérêts
    if (event._count) {
      // Si l'utilisateur s'intéresse à l'événement, incrémenter le compteur
      // Sinon, le décrémenter (mais jamais en dessous de 0)
      event._count.interests = newIsInterested 
        ? (event._count.interests || 0) + 1 
        : Math.max(0, (event._count.interests || 0) - 1);
      
      // Mettre également à jour le champ interestedCount pour la cohérence
      if (event.interestedCount !== undefined) {
        event.interestedCount = event._count.interests;
      }
      
      console.log('Compteur d\'intérêts mis à jour:', event._count.interests);
    }
    
    // Appeler la fonction parente pour mettre à jour le modèle de données
    onInterestClick(event.id);
  };
  
  const handleSaveClick = () => {
    console.log('Panel - Save click - Avant:', isSaved, 'event.id:', event.id);
    
    // Inverser l'état local
    const newIsSaved = !isSaved;
    setIsSaved(newIsSaved);
    
    console.log('Panel - Save click - Après:', newIsSaved);
    
    // Mettre à jour localement le compteur de sauvegardes
    if (event._count) {
      // Si l'utilisateur sauvegarde l'événement, incrémenter le compteur
      // Sinon, le décrémenter (mais jamais en dessous de 0)
      event._count.savedBy = newIsSaved 
        ? (event._count.savedBy || 0) + 1 
        : Math.max(0, (event._count.savedBy || 0) - 1);
      
      // Mettre également à jour le champ savedByCount pour la cohérence
      if (event.savedByCount !== undefined) {
        event.savedByCount = event._count.savedBy;
      }
      
      console.log('Compteur de sauvegardes mis à jour:', event._count.savedBy);
    }
    
    // Appeler la fonction parente pour mettre à jour le modèle de données
    onSaveClick(event.id);
  };
  
  // Fonction pour copier le lien de l'événement
  const copyEventLink = () => {
    // Construire l'URL de l'événement
    // En utilisant window.location.origin pour obtenir le domaine actuel
    const eventUrl = `${window.location.origin}/events/${event.id}`;
    
    // Copier dans le presse-papier
    navigator.clipboard.writeText(eventUrl)
      .then(() => {
        // Indiquer que le lien a été copié
        setLinkCopied(true);
        
        // Réinitialiser après 3 secondes
        setTimeout(() => {
          setLinkCopied(false);
        }, 3000);
      })
      .catch(err => {
        console.error('Erreur lors de la copie du lien:', err);
      });
  };
  
  // Format date for display
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };
  
  // Get formatted date
  const formattedDate = formatDate(event.dateTime);
  
  // Extract date and time parts for display
  const dateParts = formattedDate.split(' à ');
  const dateDisplay = dateParts[0];
  const timeDisplay = dateParts[1] || '';
  
  // Function to open location in Google Maps
  const openInMaps = () => {
    // Prefer coordinates if available for more precise location
    if (event.coordinates) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${event.coordinates.lat},${event.coordinates.lon}`, '_blank');
    } else {
      // Fall back to address search if no coordinates
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`, '_blank');
    }
  };
  
  // Function to toggle description expansion
  const toggleDescriptionExpand = () => {
    setIsExpanded(!isExpanded);
  };
  
  return (
    <div className={styles.panel}>
      <div className={styles.panelContent}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={onClose} aria-label="Retour">
            <IconChevronLeft size={20} />
            <span>Retour</span>
          </button>
          <button className={styles.closeButton} onClick={onClose} aria-label="Fermer">
            <IconX size={20} />
          </button>
        </div>
        
        <div className={styles.imageContainer}>
          {event.imageUrl ? (
            <img src={event.imageUrl} alt={event.title} className={styles.eventImage} />
          ) : (
            <div className={styles.placeholderImage}>
              <IconCalendarEvent size={48} />
            </div>
          )}
          <div 
            className={styles.categoryBadge}
            style={{ 
              '--category-color': event.eventCategory ? event.eventCategory.color : categoryInfo.color 
            } as React.CSSProperties}
          >
            {event.eventCategory ? event.eventCategory.name : categoryInfo.name}
          </div>
          {distanceLabel && (
            <div className={styles.distanceBadge}>
              <IconRoute size={16} />
              {distanceLabel}
            </div>
          )}
        </div>
        
        <div className={styles.eventContent}>
          <h1 className={styles.title}>{event.title}</h1>
          
          <div className={styles.metaInfo}>
            <div className={styles.metaItem}>
              <IconCalendarEvent size={20} />
              <div>
                <div className={styles.metaLabel}>Date</div>
                <div className={styles.metaValue}>{dateDisplay}</div>
                {timeDisplay && <div className={styles.metaTime}>{timeDisplay}</div>}
              </div>
            </div>
            
            <div className={styles.metaItem}>
              <IconMapPin size={20} />
              <div>
                <div className={styles.metaLabel}>Lieu</div>
                <div className={styles.metaValue}>{event.location}</div>
                {distanceLabel && (
                  <div className={styles.distanceInfo}>
                    <IconRoute size={16} />
                    {distanceLabel}
                  </div>
                )}
                <button className={styles.openInMapsButton} onClick={openInMaps}>
                  <IconExternalLink size={16} />
                  <span>Maps</span>
                </button>
                {userLocation && event.coordinates && (
                  <a 
                    href={getDirectionsUrl() || '#'} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={styles.directionsLink}
                    aria-label="Obtenir l'itinéraire"
                  >
                    <IconRoute size={18} />
                  </a>
                )}
              </div>
            </div>
            
            <div className={styles.metaItem}>
              <IconUsers size={20} />
              <div>
                <div className={styles.metaLabel}>Participants</div>
                <div className={styles.metaValue}>{event.attendeeCount || 0} personnes intéressées</div>
              </div>
            </div>
            
            <div className={styles.metaItem}>
              <IconTag size={20} />
              <div>
                <div className={styles.metaLabel}>Catégorie</div>
                <div className={styles.metaValue}>
                  {event.eventCategory ? event.eventCategory.name : categoryInfo.name}
                </div>
              </div>
            </div>
            
            <div className={styles.metaItem}>
              <IconEye size={20} />
              <div>
                <div className={styles.metaLabel}>Vues</div>
                <div className={styles.metaValue}>{viewCount}</div>
              </div>
            </div>
          </div>
          
          <div className={styles.description}>
            <h2 className={styles.sectionTitle}>
              <IconInfoCircle size={20} />
              Description
            </h2>
            <div className={`${styles.descriptionContent} ${isExpanded ? styles.expanded : ''}`}>
              {event.description || "Aucune description disponible pour cet événement."}
            </div>
            {event.description && event.description.length > 150 && (
              <button 
                className={styles.expandButton} 
                onClick={toggleDescriptionExpand}
              >
                {isExpanded ? 'Voir moins' : 'Voir plus'}
              </button>
            )}
            
            <div className={styles.actionBar}>
              <button 
                className={`${styles.actionButton} ${isInterested ? styles.interested : ''}`}
                onClick={handleInterestClick}
                style={isInterested ? { 
                  color: '#0066cc', 
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(0, 102, 204, 0.1)',
                  border: '1px solid #0066cc'
                } : {}}
              >
                <IconHeart size={20} fill={isInterested ? "#0066cc" : "none"} />
                <span>{isInterested ? "Intéressé" : "Ça m'intéresse"}</span>
              </button>
              
              <button 
                className={`${styles.actionButton} ${isSaved ? styles.saved : ''}`}
                onClick={handleSaveClick}
                style={isSaved ? { 
                  color: '#0066cc', 
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(0, 102, 204, 0.1)',
                  border: '1px solid #0066cc'
                } : {}}
              >
                <IconBookmark size={20} fill={isSaved ? "#0066cc" : "none"} />
                <span>{isSaved ? "Sauvegardé" : "Sauvegarder"}</span>
              </button>
              
              <button 
                className={`${styles.actionButton} ${linkCopied ? styles.linkCopied : ''}`}
                onClick={copyEventLink}
              >
                {linkCopied ? <IconCheck size={20} /> : <IconCopy size={20} />}
                <span>{linkCopied ? "Lien copié" : "Copier le lien"}</span>
              </button>
            </div>
          </div>
          
          {event.organization && (
            <div className={styles.organization}>
              <h2 className={styles.sectionTitle}>Organisation</h2>
              <div className={styles.organizationContent}>
                <div className={styles.organizationName}>{event.organization.name}</div>
                {event.organization.description && (
                  <p className={styles.organizationDescription}>{event.organization.description}</p>
                )}
              </div>
            </div>
          )}
          
          {event.organizerName && (
            <div className={styles.organizer}>
              <h2 className={styles.sectionTitle}>Organisateur</h2>
              <div className={styles.organizerContent}>
                {event.organizerName}
                {event.organizerDescription && (
                  <p className={styles.organizerDescription}>{event.organizerDescription}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventDetailPanel;
